import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Capacitor } from '@capacitor/core';

/*  E-PÉNZTÁRGÉP A BÖNGÉSZŐBŐL – a Kassza és az adóügyi egység (AE) közvetlen kapcsolata.
 *
 *  Három átvitel, UGYANAZ a soralapú keret (epg/firmware/src/main.c):  @<címke>|<PARANCS>|…*<CRC16-CCITT 4 hex>\n
 *    usb  – USB-C, WebSerial (Chrome/Edge asztalon): a gép CDC soros portja
 *    ble  – Bluetooth LE, Web Bluetooth (Chrome Androidon és asztalon): Nordic UART Service (NUS) – a gép BLE-modulja
 *           (ESP32-C3) a kereteket változatlanul továbbítja az MCU-nak
 *    iker – a digitális iker (epg/gateway): WebSocket /panel/serial – ugyanaz a bájtfolyam, mint a valódi gépen
 *  A címke „w”-vel kezdődik (az ikeren ez kötelező, a valódi gépen mindegy). A válasz a firmware nyers kerete: a Kassza
 *  változatlanul adja tovább a szervernek, az ellenőrzi (CRC, nyugta-azonosító) és rögzíti (szamlazo.js /ae-valasz).
 *  A Capacitoros mobilappban (WebView) sem WebSerial, sem Web Bluetooth nincs: ott a `ble` a natív BLE-bővítményen
 *  (@capacitor-community/bluetooth-le) megy, ugyanazzal a NUS-kerettel – a mobil Kassza így helyettesíti a gépet
 *  (user, 2026-10-12). USB a mobilappban nincs (az iOS nem enged soros USB-t appnak). */
export type AeAtvitel = 'usb' | 'ble' | 'iker';
const NUS = '6e400001-b5a3-f393-e0a9-e50e24dcca9e', NUS_RX = '6e400002-b5a3-f393-e0a9-e50e24dcca9e', NUS_TX = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';
const TAROLO = 'cronomit-ae-eszkoz';

export function crc16(s: string): string {
  let c = 0xFFFF;
  for (const x of new TextEncoder().encode(s)) { c ^= x << 8; for (let i = 0; i < 8; i++) c = c & 0x8000 ? ((c << 1) ^ 0x1021) & 0xFFFF : (c << 1) & 0xFFFF; }
  return c.toString(16).toUpperCase().padStart(4, '0');
}
/** A firmware válasza (kulcs=érték mezők) – csak a felület kijelzéséhez; a rögzítéshez a szerver értelmez. */
export function valaszBont(nyers: string): { ok: boolean; result?: any; error?: string } {
  const csillag = nyers.lastIndexOf('*');
  const torzs = csillag >= 0 ? nyers.slice(0, csillag) : nyers;
  const [, statusz, ...mezok] = torzs.split('|');
  if (statusz === 'ERR') return { ok: false, error: mezok.join('|') };
  const r: any = {};
  for (const m of mezok) { const i = m.indexOf('='); if (i > 0) { const k = m.slice(0, i), v = m.slice(i + 1); r[k] = /^-?\d+$/.test(v) && !['hash', 'head', 'rid', 'digest'].includes(k) ? Number(v) : v; } }
  return { ok: statusz === 'OK', result: r };
}

@Injectable({ providedIn: 'root' })
export class AeEszkozService {
  atvitel: AeAtvitel | null = null;
  nev = '';
  readonly esemeny$ = new Subject<string>();              // kérés nélküli firmware-események (!EVT|…)
  readonly allapot$ = new Subject<boolean>();             // kapcsolódott / bontott
  private ir: ((s: string) => Promise<void>) | null = null;
  private bont_: (() => Promise<void>) | null = null;
  private buf = '';
  private varo = new Map<string, (sor: string) => void>();
  private szamlalo = 0;
  private sor = Promise.resolve();

  get kapcsolva() { return !!this.ir; }
  /** Mit tud a böngésző? */
  tud(a: AeAtvitel): boolean {
    const n: any = navigator;
    if (Capacitor.isNativePlatform()) return a !== 'usb';
    return a === 'usb' ? !!n.serial : a === 'ble' ? !!n.bluetooth : typeof WebSocket !== 'undefined';
  }
  ikerBeallitas(): { url: string; kulcs: string } {
    try { return JSON.parse(localStorage.getItem(TAROLO) || '{}').iker || { url: '', kulcs: '' }; } catch { return { url: '', kulcs: '' }; }
  }
  ikerMent(url: string, kulcs: string) { try { localStorage.setItem(TAROLO, JSON.stringify({ iker: { url, kulcs } })); } catch { /* privát ablak */ } }

  /** Csatlakozás (felhasználói gesztusból kell hívni: a böngésző eszközválasztót mutat). */
  async csatlakozik(a: AeAtvitel): Promise<void> {
    await this.bont();
    if (a === 'usb') await this.usb();
    else if (a === 'ble') await (Capacitor.isNativePlatform() ? this.bleNativ() : this.ble());
    else await this.iker();
    this.atvitel = a; this.allapot$.next(true);
  }
  async bont() {
    const b = this.bont_; this.ir = null; this.bont_ = null; this.atvitel = null; this.nev = '';
    for (const [, v] of this.varo) v('');
    this.varo.clear();
    if (b) { try { await b(); } catch { /* már bontva */ } this.allapot$.next(false); }
  }

  /** Egy parancs a gépnek (pl. 'STATUS', 'OPEN', 'SALE|…'); a válasz a firmware nyers kerete (CRC-vel). */
  kuld(torzs: string, ido = 15000): Promise<string> {
    const fut = () => new Promise<string>((res, rej) => {
      if (!this.ir) return rej(new Error('Az e-pénztárgép nincs csatlakoztatva.'));
      const cimke = `w${++this.szamlalo}`;
      const keret = `@${cimke}|${torzs}`;
      const t = setTimeout(() => { this.varo.delete(cimke); rej(new Error('Az e-pénztárgép nem válaszolt időben.')); }, ido);
      this.varo.set(cimke, (sor) => { clearTimeout(t); sor ? res(sor) : rej(new Error('A kapcsolat megszakadt.')); });
      this.ir(`${keret}*${crc16(keret)}\n`).catch((e) => { clearTimeout(t); this.varo.delete(cimke); rej(e); });
    });
    const p = this.sor.then(fut, fut);                    // a firmware egyszálú: egyszerre egy kérés
    this.sor = p.then(() => undefined, () => undefined);
    return p;
  }

  private fogad(darab: string) {
    this.buf += darab;
    let i: number;
    while ((i = this.buf.indexOf('\n')) >= 0) {
      const sor = this.buf.slice(0, i).replace(/\r$/, ''); this.buf = this.buf.slice(i + 1);
      if (sor.startsWith('!EVT|')) { this.esemeny$.next(sor); continue; }
      const m = /^@([^|]+)\|/.exec(sor);
      const v = m && this.varo.get(m[1]);
      if (v) { this.varo.delete(m![1]); v(sor); }
    }
  }

  // ── USB-C: WebSerial ──
  private async usb() {
    const port: any = await (navigator as any).serial.requestPort({ filters: [{ usbVendorId: 0x0483 }] });   // STMicroelectronics
    await port.open({ baudRate: 115200 });
    const iro = port.writable.getWriter(), olvaso = port.readable.getReader(), dek = new TextDecoder();
    let el = true;
    (async () => { try { while (el) { const { value, done } = await olvaso.read(); if (done) break; this.fogad(dek.decode(value, { stream: true })); } } catch { /* bontva */ } if (el) this.bont(); })();
    this.ir = (s) => iro.write(new TextEncoder().encode(s));
    this.bont_ = async () => { el = false; try { await olvaso.cancel(); } catch { } olvaso.releaseLock(); iro.releaseLock(); await port.close(); };
    this.nev = 'USB-C';
  }

  // ── BLE: Web Bluetooth, Nordic UART Service (20 bájtos darabokban írva) ──
  private async ble() {
    const eszkoz: any = await (navigator as any).bluetooth.requestDevice({ filters: [{ services: [NUS] }, { namePrefix: 'Cronomit' }], optionalServices: [NUS] });
    const srv = await (await eszkoz.gatt.connect()).getPrimaryService(NUS);
    const rx = await srv.getCharacteristic(NUS_RX), tx = await srv.getCharacteristic(NUS_TX);
    const dek = new TextDecoder();
    tx.addEventListener('characteristicvaluechanged', (e: any) => this.fogad(dek.decode(e.target.value, { stream: true })));
    await tx.startNotifications();
    eszkoz.addEventListener('gattserverdisconnected', () => this.bont());
    this.ir = async (s) => { const b = new TextEncoder().encode(s); for (let i = 0; i < b.length; i += 20) await rx.writeValue(b.slice(i, i + 20)); };
    this.bont_ = async () => { try { await tx.stopNotifications(); } catch { } eszkoz.gatt.disconnect(); };
    this.nev = `Bluetooth: ${eszkoz.name || 'e-pénztárgép'}`;
  }

  // ── BLE a mobilappban: natív bővítmény, ugyanaz a NUS-keret (20 bájtos darabokban írva) ──
  private async bleNativ() {
    const { BleClient } = await import('@capacitor-community/bluetooth-le');
    //  Android 12+: a keresés nem kér helyengedélyt (a manifestben „neverForLocation”).
    await BleClient.initialize({ androidNeverForLocation: true });
    const eszkoz = await BleClient.requestDevice({ namePrefix: 'Cronomit', optionalServices: [NUS] });
    const id = eszkoz.deviceId, dek = new TextDecoder();
    await BleClient.connect(id, () => { if (this.atvitel === 'ble') this.bont(); });
    await BleClient.startNotifications(id, NUS, NUS_TX, (v) => this.fogad(dek.decode(v, { stream: true })));
    this.ir = async (s) => {
      const b = new TextEncoder().encode(s);
      for (let i = 0; i < b.length; i += 20) { const d = b.slice(i, i + 20); await BleClient.write(id, NUS, NUS_RX, new DataView(d.buffer, d.byteOffset, d.byteLength)); }
    };
    this.bont_ = async () => { try { await BleClient.stopNotifications(id, NUS, NUS_TX); } catch { } await BleClient.disconnect(id); };
    this.nev = `Bluetooth: ${eszkoz.name || 'e-pénztárgép'}`;
  }

  // ── digitális iker: WebSocket a virtuális soros portra ──
  private iker(): Promise<void> {
    const { url, kulcs } = this.ikerBeallitas();
    if (!/^https?:\/\//i.test(url)) return Promise.reject(new Error('Add meg a digitális iker címét (pl. http://szerver:8765).'));
    const ws = new WebSocket(url.replace(/^http/i, 'ws').replace(/\/+$/, '') + `/panel/serial${kulcs ? `?key=${encodeURIComponent(kulcs)}` : ''}`);
    return new Promise((res, rej) => {
      ws.onopen = () => {
        this.ir = async (s) => ws.send(s);
        this.bont_ = async () => ws.close();
        this.nev = 'Digitális iker';
        res();
      };
      ws.onerror = () => rej(new Error('A digitális iker nem érhető el (cím, kulcs?).'));
      ws.onmessage = (m) => this.fogad(String(m.data));
      ws.onclose = () => { if (this.atvitel === 'iker') this.bont(); };
    });
  }
}
